import { useRef, useEffect, useState } from "react";
import { parseResolution } from "../../../utils/canvaUtils";

const renderElement = (element, index) => {
  const baseStyle = {
    position: "absolute",
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    transform: `rotate(${element.rotation || 0}deg)`,
    transformOrigin: "center",
    overflow: "hidden",
    pointerEvents: "none",
  };

  const type = element.type;

  if (type === "text") {
    const content = element.text || element.content || "";
    const verticalAlign =
      element.verticalAlign === "top"
        ? "flex-start"
        : element.verticalAlign === "bottom"
        ? "flex-end"
        : "center";
    const horizontalAlign =
      element.textAlign === "center"
        ? "center"
        : element.textAlign === "right"
        ? "flex-end"
        : "flex-start";

    return (
      <div key={element.id ?? index} style={baseStyle}>
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: verticalAlign,
            justifyContent: horizontalAlign,
            textAlign: element.textAlign || "left",
            color: element.fill || element.color || "#000000",
            fontSize: `${element.fontSize || 16}px`,
            fontFamily: element.fontFamily || "Arial",
            fontWeight: element.fontWeight || "normal",
            fontStyle: element.fontStyle || "normal",
            lineHeight: 1.2,
            padding: "2px",
            boxSizing: "border-box",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  if (type === "rectangle" || (type === "shape" && element.shapeType !== "circle")) {
    return (
      <div
        key={element.id ?? index}
        style={{ ...baseStyle, backgroundColor: element.fill || "#000000" }}
      />
    );
  }

  if (type === "circle" || (type === "shape" && element.shapeType === "circle")) {
    return (
      <div
        key={element.id ?? index}
        style={{ ...baseStyle, backgroundColor: element.fill || "#000000", borderRadius: "50%" }}
      />
    );
  }

  if (type === "image") {
    return (
      <div key={element.id ?? index} style={baseStyle}>
        <img
          src={element.src}
          alt=""
          crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </div>
    );
  }

  if (type === "qr") {
    const fill = (element.fill || "#000000").replace("#", "");
    const src = `https://api.qrserver.com/v1/create-qr-code/?size=${element.width}x${element.height}&data=${encodeURIComponent(element.url || "https://example.com")}&color=${fill}`;
    return (
      <div key={element.id ?? index} style={baseStyle}>
        <img
          src={src}
          alt="QR"
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </div>
    );
  }

  if (type === "line") {
    const isHorizontal = element.direction !== "vertical";
    return (
      <div
        key={element.id ?? index}
        style={{ ...baseStyle, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <div
          style={{
            width: isHorizontal ? "100%" : "2px",
            height: isHorizontal ? "2px" : "100%",
            backgroundColor: element.fill || "#000000",
          }}
        />
      </div>
    );
  }

  if (type === "labels" || type === "icons") {
    const count = Math.min(element.maxCount || 3, 8);
    const isRow = element.direction !== "column";
    const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };
    const justify = justifyMap[element.alignment] || "flex-start";
    const itemSize = isRow
      ? Math.min(element.height * 0.7, (element.width / count) * 0.75)
      : Math.min(element.width * 0.7, (element.height / count) * 0.75);

    return (
      <div
        key={element.id ?? index}
        style={{
          ...baseStyle,
          display: "flex",
          flexDirection: isRow ? "row" : "column",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: justify,
          gap: Math.max(1, itemSize * 0.15),
          padding: 2,
          boxSizing: "border-box",
          backgroundColor: "rgba(200,200,200,0.15)",
          border: "1px dashed #aaa",
        }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            style={{
              width: itemSize,
              height: itemSize,
              borderRadius: "50%",
              backgroundColor: "#b0b8c1",
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    );
  }

  return null;
};

const TemplatePreviewCanvas = ({ elements, resolution }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const { width: canvasW, height: canvasH } = parseResolution(resolution || "296x128");

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      if (!clientWidth || !clientHeight) return;
      const s = Math.min(clientWidth / canvasW, clientHeight / canvasH);
      setScale(s > 0 ? s : 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [canvasW, canvasH]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          width: Math.round(canvasW * scale),
          height: Math.round(canvasH * scale),
          flexShrink: 0,
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: `${canvasW}px`,
            height: `${canvasH}px`,
            position: "relative",
            backgroundColor: "#d1d1d1",
          }}
        >
          {(elements || []).map((el, i) => renderElement(el, i))}
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewCanvas;
